/**
 * Quem fornece a foto de satélite do fundo e as ruas e nomes por cima dela, com
 * os créditos que cada um exige. Módulo puro, sem OpenLayers, para a
 * exportação, as legendas e a página dos termos importarem sem puxar o mapa.
 * Ver fundoLayer.ts, docs/fundo-satelite.md e docs/fundo-esri.md.
 *
 * - **Esri** (desde 04/11/2026, quando há chave): o World Imagery, com fotos de
 *   ~0,3–1 m da Vantor (ex-Maxar) e outros, e os rótulos "Imagery labels".
 *   Usa a chave gratuita da ArcGIS Location Platform (`VITE_ESRI_KEY`, 2
 *   milhões de blocos por mês), restrita por referenciador aos domínios do
 *   AmaView. A Esri pede "Powered by Esri" e as fontes de cada serviço no mapa.
 * - **EOX**, a reserva, sem chave: o EOxCloudless, um mosaico do Sentinel-2 de
 *   10 m (CC BY-NC-SA 4.0), com os rótulos do OpenStreetMap feitos pela EOX.
 *   Entra se o build não tiver a chave.
 */

/** Ano do mosaico do EOxCloudless (a reserva). Entra no endereço e no crédito. */
export const EOX_ANO = 2025;

/**
 * Marca dos créditos do fundo no controle de atribuição do mapa: o CSS mostra
 * só os itens com ela (index.css, `.credito-fundo`); os demais vão para o "Sobre".
 */
const MARCA = 'credito-fundo';

export interface ProvedorFundo {
  id: 'esri' | 'eox';
  /** Nome curto, para legendas ("fundo: …"). */
  nome: string;
  fotoUrl: string;
  /** Último nível pedido; dali para dentro o mapa amplia. */
  fotoMaxZoom: number;
  fotoAttribution: string;
  rotulosUrl: string;
  /** Lado do bloco dos rótulos (a Esri serve 512 px; a EOX, 256). */
  rotulosTileSize: number;
  rotulosMaxZoom: number;
  /**
   * Até quantos níveis além do último bloco os rótulos aparecem ampliados
   * (Infinity = sempre). Os da EOX param no z14 e, ampliados mais de ~1,5 vez,
   * viram faixas de letras.
   */
  rotulosNiveisAmpliados: number;
  rotulosAttribution: string;
  /** Texto puro dos créditos, para as imagens e vídeos exportados. */
  fotoCreditoTexto: string;
  rotulosCreditoTexto: string;
  /** Zoom máximo do mapa principal, em níveis da vista (createMap.ts). */
  mapaMaxZoom: number;
  /** De quando é a foto, para a legenda da denúncia. */
  dataDaFoto: string;
  /** Crédito curto, para legendas apertadas (a denúncia no topo da página). */
  creditoCurto: string;
  /** No 404 (sem foto naquele nível), usa o bloco de um nível acima, ampliado. */
  recuaNo404: boolean;
}

const ESRI_FONTES_FOTO = 'Esri, Vantor, GeoEye, Earthstar Geographics, CNES/Airbus DS, USDA, USGS, AeroGRID, IGN, and the GIS User Community';
const ESRI_FONTES_ROTULOS = 'Esri, TomTom, Garmin, FAO, NOAA, USGS, © OpenStreetMap contributors, and the GIS User Community';

/** A Esri, com a chave (que vai no endereço de cada bloco). */
export function provedorEsri(chave: string): ProvedorFundo {
  const token = encodeURIComponent(chave);
  return {
    id: 'esri',
    nome: 'Esri World Imagery',
    fotoUrl: `https://ibasemaps-api.arcgis.com/arcgis/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}?token=${token}`,
    // Nas cidades a foto vai ao z19 (~30 cm, telhados e copas); na Amazônia rural, só ao z17
    // (~1,2 m): ali o z18 e o z19 dão 404, e o bloco de cima entra ampliado (`recuaNo404`).
    fotoMaxZoom: 19,
    recuaNo404: true,
    fotoAttribution:
      `<span class="${MARCA}">Powered by <a href="https://www.esri.com" target="_blank" rel="noopener">Esri</a>` +
      ` · Imagem: ${ESRI_FONTES_FOTO}</span>`,
    rotulosUrl: `https://static-map-tiles-api.arcgis.com/arcgis/rest/services/static-basemap-tiles-service/v1/arcgis/imagery/labels/static/tile/{z}/{y}/{x}?token=${token}`,
    rotulosTileSize: 512,
    rotulosMaxZoom: 22,
    rotulosNiveisAmpliados: Infinity,
    rotulosAttribution: `<span class="${MARCA}">Rótulos: ${ESRI_FONTES_ROTULOS}</span>`,
    fotoCreditoTexto: `Powered by Esri; imagem: ${ESRI_FONTES_FOTO}`,
    rotulosCreditoTexto: `rótulos: ${ESRI_FONTES_ROTULOS}`,
    mapaMaxZoom: 17,
    dataDaFoto: 'mosaico de fotos de datas variadas; pode ter nuvens',
    creditoCurto: 'Powered by Esri · World Imagery (Esri, Vantor e outros)',
  };
}

/** A EOX (Sentinel-2 cloudless), sem chave: a reserva. */
export function provedorEox(): ProvedorFundo {
  const wmts = 'https://tiles.maps.eox.at/wmts/1.0.0';
  return {
    id: 'eox',
    nome: `EOxCloudless ${EOX_ANO} (Sentinel-2)`,
    fotoUrl: `${wmts}/s2cloudless-${EOX_ANO}_3857/default/GoogleMapsCompatible/{z}/{y}/{x}.jpg`,
    // O z14 (~9,5 m) já é a resolução do Sentinel-2.
    fotoMaxZoom: 14,
    // O crédito que a licença pede (https://cloudless.eox.at/documentation/license).
    fotoAttribution:
      `<span class="${MARCA}"><a href="https://cloudless.eox.at" target="_blank" rel="noopener">EOxCloudless</a> by ` +
      `<a href="https://eox.at" target="_blank" rel="noopener">EOX IT Services GmbH</a> ` +
      `(Contains modified Copernicus Sentinel data ${EOX_ANO})</span>`,
    rotulosUrl: `${wmts}/overlay_bright_3857/default/GoogleMapsCompatible/{z}/{y}/{x}.png`,
    rotulosTileSize: 256,
    // Do z15 em diante o servidor responde 404.
    rotulosMaxZoom: 14,
    rotulosNiveisAmpliados: 0.4,
    rotulosAttribution:
      `<span class="${MARCA}">Ruas: <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">© OpenStreetMap</a>` +
      `, <a href="https://maps.eox.at" target="_blank" rel="noopener">EOX::Maps</a></span>`,
    fotoCreditoTexto: `EOxCloudless ${EOX_ANO} (cloudless.eox.at) by EOX IT Services GmbH, contains modified Copernicus Sentinel data ${EOX_ANO}, CC BY-NC-SA 4.0`,
    rotulosCreditoTexto: 'ruas e nomes © OpenStreetMap contributors, rendering EOX::Maps',
    // A foto de 10 m ampliada ~6 vezes; mais perto, só borrão.
    mapaMaxZoom: 14,
    dataDaFoto: `mosaico sem nuvens de ${EOX_ANO}`,
    creditoCurto: `EOxCloudless ${EOX_ANO} by EOX IT Services GmbH (Copernicus Sentinel)`,
    recuaNo404: false,
  };
}

/** Esri se o build tem a chave; senão, a EOX. */
export function provedorDoFundo(chave: string | undefined): ProvedorFundo {
  const c = chave?.trim();
  return c ? provedorEsri(c) : provedorEox();
}

/** O provedor deste build. */
export const PROVEDOR_FUNDO: ProvedorFundo = provedorDoFundo(import.meta.env.VITE_ESRI_KEY as string | undefined);

export const FOTO_ATTRIBUTION = PROVEDOR_FUNDO.fotoAttribution;
export const ROTULOS_ATTRIBUTION = PROVEDOR_FUNDO.rotulosAttribution;
export const FOTO_CREDITO_TEXTO = PROVEDOR_FUNDO.fotoCreditoTexto;
export const ROTULOS_CREDITO_TEXTO = PROVEDOR_FUNDO.rotulosCreditoTexto;
